import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  PawPrint,
  Search,
  Filter,
  Heart,
  Home,
  LogIn,
  ChevronLeft,
  X,
  Calendar,
  Utensils,
  Stethoscope
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { useLocalize } from "@/lib/localize";
import { useDogsList } from "@/hooks/useDogsList";

// Pure helpers — component scope dışına alındı (lint react-hooks/immutability'i tetiklemesin diye)
const STATUS_LABELS = {
  barinakta: "Alanda",
  sahiplendirmeye_hazir: "Sahiplenmeye Hazır",
  sahiplendirildi: "Sahiplendirildi",
  // 1:N model — bir cana hâlâ daha fazla Koruyucu Aile katılabilir,
  // bu yüzden "Koruyucu Ailesi Var" yerine nötr "Bakımda" gösteriyoruz
  koruyucu_ailede: "Geçici Bakımda",
  tedavi_goruyor: "Tedavi Görüyor",
  transfer: "Transferde",
  beklemede: "Beklemede",
  kayip: "Kayıp",
  vefat: "Vefat",
};
const getStatusClass = (status) => {
  if (status === "sahiplendirmeye_hazir") return "status-available";
  if (status === "sahiplendirildi") return "status-adopted";
  // Bakımda renkleri sahiplenmeye_hazir ile aynı (nötr ve davetkar)
  if (status === "koruyucu_ailede") return "status-available";
  if (status === "tedavi_goruyor") return "bg-amber-100 text-amber-800";
  if (status === "transfer") return "bg-blue-100 text-blue-800";
  return "bg-muted text-muted-foreground";
};
const SIZE_LABELS = { small: "Küçük", medium: "Orta", large: "Büyük", giant: "Dev" };

export default function DogsPage() {
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get("filter");
  const supportParam = searchParams.get("support");
  const needsGuardianMode = filterParam === "needs_guardian";

  const [showFilters, setShowFilters] = useState(false);
  const [nextEvent, setNextEvent] = useState(null);
  const [eventDogIds, setEventDogIds] = useState(new Set());
  const { t } = useContent();
  const L = useT();
  const Lo = useLocalize();
  const getStatusLabel = (status) => L(STATUS_LABELS[status] || status);
  const getSizeLabel = (size) => L(SIZE_LABELS[size] || size);
  
  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [size, setSize] = useState("all");
  const [isChildFriendly, setIsChildFriendly] = useState(false);
  const [isApartmentSuitable, setIsApartmentSuitable] = useState(false);

  // 🔁 Köpek listesi async data → custom hook (component scope'da setState antipattern'i tetiklemesin)
  const { dogs, total, loading } = useDogsList({
    search, status, size, isChildFriendly, isApartmentSuitable, needsGuardianMode,
  });

  useEffect(() => {
    axios
      .get(`${API}/public/adoption-events/next`)
      .then((r) => {
        const ev = r.data.event;
        setNextEvent(ev);
        setEventDogIds(new Set(ev?.featured_dog_ids || []));
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav activeKey="canlar" />

      {/* Header */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            {L("Ana Sayfa")}
          </Link>
          
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-foreground mb-4">
            {needsGuardianMode
              ? L("💛 Koruyucu Aile Arayan Canlar")
              : supportParam === "meal"
              ? L("🍲 Bekleyen Bir Cana Mama Desteği")
              : supportParam === "vet"
              ? L("⚕️ Bir Cana Veteriner Desteği")
              : status === "sahiplendirildi"
              ? L("🎉 Mutlu Hikayeler")
              : t("dogs.page.title", L("Seni Bekleyen Canlar"))}{" "}
            ({total})
          </h1>
          <p className="text-lg text-muted-foreground">
            {needsGuardianMode
              ? L("Bir cana birden fazla destekçi olabilir — kalabalıkken bile yer var 💛 Aşağıda mevcut destekçi sayısı az olanlar üstte sıralı.")
              : supportParam === "meal"
              ? L("Aşağıdan bir can seç ve mama desteğini doğrudan ona yönlendir. Bir tabak, somut bir fark.")
              : supportParam === "vet"
              ? L("Aşağıdan bir can seç ve veteriner desteğini onun sağlığına yönlendir. Aşı, kısırlaştırma, tedavi — hepsi sürüyor.")
              : status === "sahiplendirildi"
              ? L("Yuvasına kavuşan canlarımızın hikâyeleri. Her birinin kendi sevgi yolculuğu burada başladı.")
              : t("dogs.page.description", L("Her biri benzersiz bir hikaye, her biri yeni bir başlangıç bekliyor"))}
          </p>

          {nextEvent && eventDogIds.size > 0 && (
            <Link
              to="/etkinlikler"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 hover:bg-amber-200 transition-colors text-sm font-medium"
              data-testid="next-event-cta"
            >
              <Calendar className="w-4 h-4" />
              {L("Bu hafta {n} can {loc}'ta — etkinliği gör").replace("{n}", eventDogIds.size).replace("{loc}", nextEvent.location_name)}
            </Link>
          )}
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-6 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={(e) => e.preventDefault()} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={L("İsim veya ırk ara…")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" size="lg" className="btn-pill bg-primary text-primary-foreground transition-all duration-200 hover:bg-primary hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30" data-testid="search-btn">
                {L("Ara")}
              </Button>
            </form>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowFilters(!showFilters)}
              className="btn-pill border-2"
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              {L("Filtreler")}
              {(status !== "all" || size !== "all" || isChildFriendly || isApartmentSuitable) && (
                <span className="ml-2 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  !
                </span>
              )}
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-6 bg-card rounded-2xl border border-border/50 animate-fade-in-up">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">{L("Filtreler")}</h3>
                <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatus("all"); setSize("all"); setIsChildFriendly(false); setIsApartmentSuitable(false); }} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-1" />
                  {L("Temizle")}
                </Button>
              </div>
              
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="mb-2 block text-sm">{L("Durum")}</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-10 rounded-xl" data-testid="status-filter">
                      <SelectValue placeholder={L("Tümü")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{L("Tümü (Açık Profiller)")}</SelectItem>
                      <SelectItem value="sahiplendirmeye_hazir">{L("Sahiplenmeye Hazır")}</SelectItem>
                      <SelectItem value="koruyucu_ailede">{L("Geçici Bakımda")}</SelectItem>
                      <SelectItem value="barinakta">{L("Alanda")}</SelectItem>
                      <SelectItem value="sahiplendirildi">{L("🎉 Mutlu Hikayeler")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-2 block text-sm">{L("Boyut")}</Label>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger className="h-10 rounded-xl" data-testid="size-filter">
                      <SelectValue placeholder={L("Tümü")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{L("Tümü")}</SelectItem>
                      <SelectItem value="small">{L("Küçük")}</SelectItem>
                      <SelectItem value="medium">{L("Orta")}</SelectItem>
                      <SelectItem value="large">{L("Büyük")}</SelectItem>
                      <SelectItem value="giant">{L("Dev")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="child-friendly"
                    checked={isChildFriendly}
                    onCheckedChange={setIsChildFriendly}
                    data-testid="child-friendly-filter"
                  />
                  <Label htmlFor="child-friendly" className="text-sm cursor-pointer">
                    {L("Çocuklarla Uyumlu")}
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="apartment-suitable"
                    checked={isApartmentSuitable}
                    onCheckedChange={setIsApartmentSuitable}
                    data-testid="apartment-filter"
                  />
                  <Label htmlFor="apartment-suitable" className="text-sm cursor-pointer">
                    {L("Apartmana Uygun")}
                  </Label>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Dogs Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : dogs.length === 0 ? (
            <div className="text-center py-20">
              <PawPrint className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-2xl mb-2">{L("Can Bulunamadı")}</h3>
              <p className="text-muted-foreground">{L("Filtreleri değiştirmeyi deneyin")}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {dogs.map((dog) => (
                <Link
                  key={dog.dog_id}
                  to={supportParam ? `/can/${dog.dog_id}?support=${supportParam}` : `/can/${dog.dog_id}`}
                  data-testid={`dog-card-${dog.dog_id}`}
                >
                  <Card className="card-hover overflow-hidden bg-card border border-border/50 shadow-xl shadow-black/5 h-full flex flex-col">
                    <div className="img-zoom aspect-[4/3] relative bg-muted/30">
                      <img
                        src={dog.photo_url || "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=400&fit=crop"}
                        alt={dog.name}
                        className="w-full h-full object-contain"
                      />
                      <span className={`absolute top-3 right-3 text-sm font-medium px-3.5 py-1.5 rounded-full ${getStatusClass(dog.status)}`}>
                        {getStatusLabel(dog.status)}
                      </span>
                      {eventDogIds.has(dog.dog_id) && (
                        <span
                          className="absolute bottom-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center gap-1 shadow-md"
                          data-testid="dog-card-event-badge"
                        >
                          <Calendar className="w-3 h-3" />
                          {L("Bu hafta Kuğulu'da")}
                        </span>
                      )}
                    </div>
                    <CardContent className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading text-xl mb-2">{dog.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {Lo(dog, "breed") || L("Karışık")} · {getSizeLabel(dog.size)} · {dog.gender === "male" ? L("Erkek") : L("Dişi")}
                      </p>
                      
                      {(Lo(dog, "story_short") || dog.story_short) && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {Lo(dog, "story_short") || L(dog.story_short)}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-2 mb-2">
                        {dog.is_child_friendly && (
                          <span className="text-xs px-2.5 py-1 bg-muted rounded-full">{L("Çocuk Dostu")}</span>
                        )}
                        {dog.is_dog_friendly && (
                          <span className="text-xs px-2.5 py-1 bg-muted rounded-full">{L("Köpek Dostu")}</span>
                        )}
                        {dog.is_apartment_suitable && (
                          <span className="text-xs px-2.5 py-1 bg-muted rounded-full">{L("Apartmana Uygun")}</span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 mt-auto pt-4">
                        {dog.status === "sahiplendirildi" ? (
                          <Button
                            variant="outline"
                            className="w-full rounded-full border-primary/40 text-primary text-sm h-11 font-semibold pointer-events-none"
                            data-testid={`dog-card-adopted-${dog.dog_id}`}
                          >
                            <Heart className="w-4 h-4 mr-2 shrink-0 fill-primary" />
                            {L("Yuvasına Kavuştu")}
                          </Button>
                        ) : supportParam === "meal" ? (
                          <Button className="w-full rounded-full bg-primary text-primary-foreground text-sm h-11" data-testid={`dog-card-meal-cta-${dog.dog_id}`}>
                            <Utensils className="w-4 h-4 mr-2 shrink-0" />
                            {L("Bu Cana Mama Desteği Ver")}
                          </Button>
                        ) : supportParam === "vet" ? (
                          <Button className="w-full rounded-full bg-primary text-primary-foreground text-sm h-11" data-testid={`dog-card-vet-cta-${dog.dog_id}`}>
                            <Stethoscope className="w-4 h-4 mr-2 shrink-0" />
                            {L("Bu Cana Veteriner Desteği Ver")}
                          </Button>
                        ) : needsGuardianMode ? (
                          <Button className="w-full rounded-full bg-secondary text-secondary-foreground text-sm h-11" data-testid={`dog-card-guardian-cta-${dog.dog_id}`}>
                            <Home className="w-4 h-4 mr-2 shrink-0" />
                            {L("Koruyucu Aile Ol")}
                          </Button>
                        ) : (
                          <>
                            <Button className="w-full rounded-full bg-primary text-primary-foreground text-sm h-11 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30">
                              <Heart className="w-4 h-4 mr-2 shrink-0" />
                              {t("dogs.card.adopt_btn", L("Sahiplen"))}
                            </Button>
                            <Button variant="outline" className="w-full rounded-full border-secondary text-secondary text-sm h-11">
                              <Home className="w-4 h-4 mr-2 shrink-0" />
                              {t("dogs.card.guardian_btn", L("Koruyucu Aile Ol"))}
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
